import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment-timezone";
import { useFormik } from "formik";
import {
  createEvent,
  updateEvent,
  deleteEvent,
  closeEventDetail,
} from "../../../store/slices/scheduleSlice";
import { getTimezoneOptions } from "../../../helpers/scheduleUtils";
import * as Yup from "yup";

const weekdayCodeMap = {
  Sun: "SU",
  Mon: "MO",
  Tue: "TU",
  Wed: "WE",
  Thu: "TH",
  Fri: "FR",
  Sat: "SA",
};

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  description: Yup.string().required("Description is required"),
  startDate: Yup.date().required("Start date is required"),
  tags: Yup.array(),
  hosts: Yup.array(),
  startTime: Yup.string().when("allDay", {
    is: false, // Only require when allDay is false
    then: () => Yup.string().required("Start time is required"),
    otherwise: () => Yup.string().notRequired()
  }),
  duration: Yup.number().when("allDay", {
    is: false, // Only validate when allDay is false
    then: () => Yup.number()
      .min(15, "Duration must be at least 15 minutes")
      .required("Duration is required"),
    otherwise: () => Yup.number().notRequired()
  }),

  allDay: Yup.boolean(),
  // Add validation for recurring fields if needed
  recurring: Yup.boolean(),
  freq: Yup.string().when("recurring", {
    is: true,
    then: () => Yup.string().required("Frequency is required for recurring events"),
  }),
  tz : Yup.string().required('TimeZone is required'),
  endDate: Yup.date().when(["recurring", "haslongduration"], {
    is: (recurring, haslongduration) => recurring && !haslongduration,
    then: () => Yup.date().required("End date is required for recurring events"),
  }),
});

export function useEventDetail({
  isOpen,
  onClose,
  refetchEvents,
  onShowSnackbar,
}) {
  const dispatch = useDispatch();
  const { selectedEvent, tags, currentDate, currentView } = useSelector(
    (state) => state.schedule
  );
  const { user } = useSelector((state) => state.auth);

  const [localError, setLocalError] = useState(null);
  const [showError, setShowError] = useState(false);
  const errorTimeout = useRef(null);
  const isEditing = !!selectedEvent?.id;
  const normalizedMonthlyWeek = Array.isArray(selectedEvent?.monthlyWeek)
    ? selectedEvent.monthlyWeek[0] || ""
    : selectedEvent?.monthlyWeek || "";
  const normalizedMonthlyDays = Array.isArray(selectedEvent?.monthlyDays)
    ? selectedEvent.monthlyDays[0] || ""
    : selectedEvent?.monthlyDays || "";
  const normalizedMonthlyWeekday = weekdayCodeMap[selectedEvent?.monthlyweekDay]
    || selectedEvent?.monthlyweekDay
    || "";

  // Initialize form with Formik
  const formik = useFormik({
    initialValues: {
      id: selectedEvent?.id || '',
      title: selectedEvent?.title || '',
      description: selectedEvent?.description || '',
      duration: selectedEvent?.duration || "",
      haslongduration: selectedEvent?.haslongduration || false,
      hosts: selectedEvent?.hosts || [],
      tz: selectedEvent?.tz || user?.attributes?.stationTimezone,
      startDate: selectedEvent?.start ? moment(selectedEvent.startDate).format('YYYY-MM-DD') : moment(new Date()).format('YYYY-MM-DD'),
      startTime: selectedEvent?.start ? moment(selectedEvent.startDate).format('HH:mm') : '',
      allDay: selectedEvent?.duration == 1440 ? true : false,
      tags: selectedEvent?.tags || [],
      recurring: selectedEvent?.recurring || false,

      // Fix the field name mismatches:
      freq: selectedEvent?.recurringType?.toUpperCase() || 'WEEKLY', // Map recurringType to freq
      days: selectedEvent?.days || [], // Map days to daysOfWeek

      endDate: selectedEvent?.endDate ? moment(selectedEvent.endDate).format('YYYY-MM-DD') : '',
      noEndDate: !selectedEvent?.endDate,

      // Add the fields that your component expects but might not be in API data
      recurringType: selectedEvent?.recurringType || 'daily',
      monthlyOption: selectedEvent?.monthlyOption || '',
      monthlyDays: normalizedMonthlyDays,
      monthlyWeek: normalizedMonthlyWeek,
      monthlyweekDay: normalizedMonthlyWeekday,
      repeatsEvery: selectedEvent?.repeatsEvery || 1,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        console.log("values", values);
        let resp =  await dispatch(createEvent(values)).unwrap();
          
        if (values.id) {
          if (onShowSnackbar) onShowSnackbar("Event Updated successfully");

        } else {
          if (onShowSnackbar) onShowSnackbar("Event created successfully");
        }
        resetForm();
        dispatch(closeEventDetail());
        if (typeof refetchEvents === "function") {
          setTimeout(() => {
            refetchEvents(currentDate, currentView);
          }, 500);
        }
      } catch (error) {
        setLocalError(
          error?.message || error?.error || "Event operation failed"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const tagOptions = useMemo(
    () =>
      tags.flatMap((section) =>
        section.suggestions.map((tag) => ({
          value: tag.id,
          label: tag.text,
          section: section.title,
        }))
      ),
    [tags]
  );

  const handleDelete = useCallback(async () => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        let token = user?.token;
        if (!token) {
          token = localStorage.getItem("authToken");
        }
        let id = selectedEvent?.id;

        if (!id) {
          setLocalError(
            "Delete failed: Missing or invalid event ID. See console for details."
          );
          return;
        }
        await dispatch(deleteEvent({ token, id })).unwrap();
        if (onShowSnackbar) onShowSnackbar("Event deleted successfully");
        dispatch(closeEventDetail());
        if (typeof refetchEvents === "function") {
          setTimeout(() => {
            refetchEvents(currentDate, currentView);
          }, 500);
        }
      } catch (error) {
        setLocalError(error?.message || error?.error || "Delete failed");
      }
    }
  }, [
    dispatch,
    selectedEvent,
    user,
    refetchEvents,
    currentDate,
    currentView,
    onShowSnackbar,
  ]);

  return {
    values: formik.values,
    setFieldValue: formik.setFieldValue,
    handleSubmit: formik.handleSubmit,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    isSubmitting: formik.isSubmitting,
    errors: formik.errors,
    touched: formik.touched,
    submitCount: formik.submitCount,
    resetForm: formik.resetForm, // Add this line
    showError,
    localError,
    setShowError,
    setLocalError,
    isEditing,
    handleDelete,
    tagOptions,
    tags,
    currentDate,
    currentView,
  };
}